<template>
  <div class="direct-read-card">
    <!-- Title -->
    <h1 v-if="card.title" class="card-title">{{ card.title }}</h1>

    <!-- Labels/Tags -->
    <div v-if="cardCollections.length > 0 || (card.labels && card.labels.length > 0)" class="card-labels">
      <el-tag
        v-for="collection in cardCollections"
        :key="`collection-${collection.id}`"
        size="small"
        type="primary"
        effect="light"
        class="label-tag collection-tag"
      >
        {{ collection.name }}
      </el-tag>
      <el-tag
        v-for="(label, index) in card.labels"
        :key="`label-${index}`"
        size="small"
        type="info"
        effect="light"
        class="label-tag"
      >
        {{ label }}
      </el-tag>
    </div>

    <!-- Content based on type -->
    <div class="card-content">
      <!-- Text Content -->
      <template v-if="!card.content_type || card.content_type === 'text'">
        <div class="text-content" v-html="renderContent()"></div>
      </template>

      <!-- Quote Content -->
      <template v-else-if="card.content_type === 'quote'">
        <figure class="quote-content">
          <div class="quote-mark" aria-hidden="true">&ldquo;</div>
          <blockquote v-html="renderQuoteContent()"></blockquote>
        </figure>
      </template>

      <!-- Key Terms Content -->
      <template v-else-if="isKeyTermsCard(card)">
        <KeyTermsCard :card="card" />
      </template>

      <!-- Image Content -->
      <template v-else-if="card.content_type === 'image'">
        <div class="image-content">
          <img v-if="card.fileList && card.fileList.length > 0"
            :src="card.fileList[0].url"
            :alt="'Card Image'"
          >
        </div>
      </template>

      <!-- Video Content -->
      <template v-else-if="card.content_type === 'video'">
        <div class="video-content">
          <video v-if="card.fileList && card.fileList.length > 0" controls>
            <source :src="card.fileList[0].url" :type="card.fileList[0].mime_type">
            Your browser does not support the video tag.
          </video>
        </div>
      </template>

      <!-- Quiz Content -->
      <template v-else-if="card.content_type === 'quiz'">
        <div class="quiz-content">
          <div class="quiz-question"><strong>Question:</strong> <span>{{ card.quiz_content.question }}</span></div>
          <div v-for="(option, idx) in card.quiz_content.options.filter(option => option !== '')" :key="idx" class="quiz-option">
            <div class="quiz-option-content">
              <div style="display: flex; flex: 1; align-items: flex-start;">
                <span style="min-width: 30px; line-height: 1.6;">{{ ['A','B','C','D'][idx] }}.</span>
                <span style="line-height: 1.6;">{{ option }}</span>
              </div>
            </div>
          </div>
          <div v-if="showAnswer" class="quiz-explanation">
            <div class="explanation-header">Explanation:</div>
            <div class="explanation-content">{{ card.quiz_content.explanation }}</div>
          </div>
        </div>
      </template>

      <!-- Resources Section -->
      <div v-if="card.resources && card.resources.length > 0" class="card-resources">
        <div class="resources-divider"></div>
        <div class="resources-header">
          <i class="el-icon-notebook-2"></i>
          <span>Resources</span>
        </div>
        <div class="resources-citations">
          <div v-for="(resource, index) in card.resources" :key="index" class="resource-citation">
            <span class="citation-number">[{{ index + 1 }}]</span>
            <span class="citation-content">
              <template v-if="resource.author">{{ resource.author }}. </template>
              <span class="citation-title">{{ resource.name }}</span>
              <template v-if="resource.type">. <em>{{ formatResourceType(resource.type) }}</em></template>
              <template v-if="resource.url">
                . <a :href="resource.url" target="_blank" rel="noopener noreferrer" class="citation-link">
                  <i class="el-icon-link"></i> Link
                </a>
              </template>
              <template v-if="resource.description">
                <span class="citation-description">{{ resource.description }}</span>
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer metadata -->
    <div class="card-footer">
      <span class="card-date">{{ formatDate(card.created_at) }}</span>
      <div
        v-if="showReminder && (card.reminder_enabled || card.enable_reminder)"
        class="reminder-indicator"
      >
        <i class="el-icon-bell"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import KeyTermsCard from './KeyTermsCard.vue'

export default {
  name: 'DirectReadCard',
  components: {
    KeyTermsCard
  },
  props: {
    card: {
      type: Object,
      required: true
    },
    collections: {
      type: Array,
      default: () => []
    },
    showAnswer: {
      type: Boolean,
      default: true
    },
    showReminder: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardCollections() {
      if (!this.collections || this.collections.length === 0) {
        return []
      }

      let collectionIds = []
      if (this.card.collection_ids && Array.isArray(this.card.collection_ids)) {
        collectionIds = this.card.collection_ids
      } else if (this.card.collection_id) {
        collectionIds = [this.card.collection_id]
      }

      if (collectionIds.length === 0) {
        return []
      }

      return this.collections.filter(col => collectionIds.includes(col.id))
    },
    getCollectionTagColor() {
      return (collectionName) => {
        const colors = [
          '#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#9c27b0',
          '#00bcd4', '#ff9800', '#795548', '#607d8b', '#3f51b5'
        ]
        let hash = 0
        for (let i = 0; i < collectionName.length; i++) {
          hash = collectionName.charCodeAt(i) + ((hash << 5) - hash)
        }
        return colors[Math.abs(hash) % colors.length]
      }
    }
  },
  methods: {
    isKeyTermsCard(card) {
      const contentType = (card && card.content_type ? card.content_type : '').toString().toLowerCase()
      return ['key_terms', 'keyterms', 'key-terms', 'key_terms_card', 'keyterms_card', 'key-terms-card', 'terms', 'term_list'].includes(contentType)
    },
    getQuoteText() {
      return (
        this.card.content ||
        this.card.quote_content ||
        this.card.quote ||
        this.card.text ||
        ''
      ).trim()
    },
    renderQuoteContent() {
      const quote = this.getQuoteText()
      if (!quote) {
        return '<p class="quote-placeholder">No quote is available for this card yet.</p>'
      }
      const rawHtml = marked(quote)
      return DOMPurify.sanitize(rawHtml)
    },
    renderContent() {
      if (!this.card.content) return ''
      const rawHtml = marked(this.card.content)
      return DOMPurify.sanitize(rawHtml)
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatResourceType(type) {
      const typeMap = {
        'book': 'Book',
        'article': 'Article',
        'website': 'Website',
        'video': 'Video',
        'paper': 'Research Paper',
        'other': 'Resource'
      }
      return typeMap[type] || type
    }
  }
}
</script>

<style scoped>
.direct-read-card {
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px;
  background: transparent;
}

.card-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  word-wrap: break-word;
}

.card-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.label-tag {
  font-size: 13px;
  padding: 6px 12px !important;
  border-radius: 4px;
  border: none;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important;
  vertical-align: middle !important;
}

.card-content {
  font-size: 17px;
  line-height: 1.7;
  color: #333;
}

/* Text Content Styling */
.text-content {
  font-size: 17px;
  line-height: 1.7;
  color: #333;
  word-wrap: break-word;
}

.text-content :deep(h1),
.text-content :deep(h2),
.text-content :deep(h3),
.text-content :deep(h4),
.text-content :deep(h5),
.text-content :deep(h6) {
  margin-top: 32px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #1a1a1a;
}

.text-content :deep(h1) { font-size: 28px; }
.text-content :deep(h2) { font-size: 24px; }
.text-content :deep(h3) { font-size: 20px; }
.text-content :deep(h4) { font-size: 18px; }
.text-content :deep(h5) { font-size: 16px; }
.text-content :deep(h6) { font-size: 15px; }

.text-content :deep(p) {
  margin: 0 0 16px 0;
  line-height: 1.7;
}

.text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.text-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.text-content :deep(em) {
  font-style: italic;
}

.text-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid #409eff;
}

.text-content :deep(a:hover) {
  color: #66b1ff;
  border-bottom-color: #66b1ff;
}

.text-content :deep(ul),
.text-content :deep(ol) {
  margin: 16px 0;
  padding-left: 28px;
}

.text-content :deep(li) {
  margin: 8px 0;
  line-height: 1.7;
}

.text-content :deep(blockquote) {
  margin: 20px 0;
  padding: 16px 20px;
  background: #f8f9fa;
  border-left: 4px solid #409eff;
  color: #606266;
}

.text-content :deep(code) {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.text-content :deep(pre) {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 16px 0;
}

.text-content :deep(pre code) {
  background: transparent;
  padding: 0;
  color: #333;
}

.text-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 16px 0;
}

.quote-content {
  margin: 0;
  min-height: 260px;
  padding: 32px 28px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.quote-mark {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 76px;
  line-height: 0.8;
  color: #d6dde8;
  margin-bottom: 12px;
}

.quote-content blockquote {
  margin: 0;
  color: #1d2939;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 30px;
  line-height: 1.45;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.quote-content :deep(p) {
  margin: 0 0 14px;
}

.quote-content :deep(p:last-child) {
  margin-bottom: 0;
}

.quote-content :deep(.quote-placeholder) {
  color: #667085;
  font-family: inherit;
  font-size: 17px;
  font-weight: 400;
}

/* Image Content */
.image-content {
  margin: 24px 0;
}

.image-content img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

/* Video Content */
.video-content {
  margin: 24px 0;
}

.video-content video {
  width: 100%;
  border-radius: 8px;
}

/* Quiz Content */
.quiz-content {
  background: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  margin: 24px 0;
}

.quiz-question {
  font-size: 18px;
  margin-bottom: 20px;
  line-height: 1.6;
}

.quiz-question strong {
  color: #409eff;
  margin-right: 8px;
}

.quiz-option {
  background: white;
  padding: 16px;
  margin: 12px 0;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.quiz-option-content {
  display: flex;
  align-items: flex-start;
  font-size: 16px;
  line-height: 1.6;
}

.quiz-explanation {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 2px solid #e4e7ed;
}

.explanation-header {
  font-weight: 600;
  color: #409eff;
  margin-bottom: 12px;
  font-size: 16px;
}

.explanation-content {
  line-height: 1.6;
  color: #606266;
}

/* Resources Section */
.card-resources {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 2px solid #e4e7ed;
}

.resources-divider {
  margin-bottom: 24px;
}

.resources-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
}

.resources-header i {
  font-size: 22px;
  color: #409eff;
}

.resources-citations {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.resource-citation {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.6;
  font-size: 15px;
}

.citation-number {
  color: #409eff;
  font-weight: 600;
  flex-shrink: 0;
}

.citation-content {
  color: #606266;
  flex: 1;
}

.citation-title {
  font-weight: 500;
  color: #303133;
}

.citation-link {
  color: #409eff;
  text-decoration: none;
  margin-left: 4px;
}

.citation-link:hover {
  text-decoration: underline;
}

.citation-description {
  display: block;
  margin-top: 4px;
  color: #909399;
  font-size: 14px;
}

/* Footer */
.card-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #e4e7ed;
}

.card-date {
  font-size: 14px;
  color: #909399;
}

.reminder-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF, #66B1FF);
  color: white;
}

.reminder-indicator i {
  font-size: 12px;
}
</style>
